/**
 * Approved parent-site content (Release 2.0.0, task T03).
 *
 * Single source of truth for the shared TickleApps surfaces: catalogue entries,
 * statuses, verified store URLs, parent-brand copy, the support address, and
 * the visible release marker. Every value here comes from the founder-approved
 * content brief `09-T03-CONTENT-BRIEF-DRAFT.md`; nothing may be invented,
 * expanded, or softened here or in the components that consume it.
 *
 * App-specific product detail, legal, privacy, and deletion copy stay with the
 * app pages (tasks T04-T06) and are not duplicated here.
 */
import releaseVersion from '../release-version.json';

// The release version has exactly one source: frontend/src/release-version.json,
// which also stamps the deployed build/release.json.
export const RELEASE_VERSION = releaseVersion.version;
export const RELEASE_MANIFEST_PATH = '/release.json';
export const RELEASE_LABEL = `Website release ${RELEASE_VERSION}`;

export const SUPPORT_EMAIL = 'support@tickleapps.com';

export const STUDIO = {
  name: 'TickleApps',
  primaryStatement:
    'TickleApps is an independent app and solutions studio based in Ahmedabad, India. We build focused mobile apps and private, local-first tools for clearer everyday decisions and more disciplined software delivery.',
  supportingStatement:
    'Each product starts with a clear problem. Product-specific support, privacy, and legal information is available with the relevant app.',
  aboutOpening:
    'Founded by Bharatkumar Leel, TickleApps is a self-funded, independent studio in Ahmedabad, India. We build practical software with a clear purpose, then maintain it with care.',
};

export const PRODUCT_STATUS = {
  live: 'Live',
  inDevelopment: 'In development',
};

/**
 * The complete public catalogue, in the order the site presents it: live
 * products first. `productPath` is an internal route; `storeUrl` is the only
 * approved external destination. A product without a store URL must not get an
 * external call to action, a waitlist, or a date.
 */
export const products = [
  {
    id: 'nutriscan',
    name: 'NutriScan',
    status: PRODUCT_STATUS.live,
    description: 'Helps people understand food labels with AI-assisted analysis.',
    productPath: '/apps/nutriscan',
    storeUrl: 'https://play.google.com/store/apps/details?id=com.tickleapps.nutriscan',
    storeLabel: 'Google Play',
    icon: 'fa-utensils',
  },
  {
    id: 'cardcycle',
    name: 'CardCycle',
    status: PRODUCT_STATUS.live,
    description:
      'Helps people keep personal card statement dates, payment due dates, and reminders organised.',
    productPath: '/apps/cardcycle',
    storeUrl: 'https://play.google.com/store/apps/details?id=com.tickleapps.cardcycle',
    storeLabel: 'Google Play',
    icon: 'fa-credit-card',
  },
  {
    id: 'heronboard',
    name: 'HeronBoard',
    status: PRODUCT_STATUS.inDevelopment,
    description: 'A private delivery ledger for teams coordinating multiple local coding agents.',
    positioning:
      'The private delivery ledger for teams that run multiple coding agents locally and need to know what changed, which agent did it, what was verified, what remains risky, and what it cost—without making local source context another SaaS data stream.',
    productPath: null,
    storeUrl: null,
    storeLabel: null,
    icon: 'fa-list-check',
  },
];

export const liveProducts = products.filter((product) => product.status === PRODUCT_STATUS.live);

export const NAV_LINKS = [
  { to: '/apps', label: 'Apps' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Support' },
];

export const FOOTER_LINKS = {
  products: liveProducts.map((product) => ({ to: product.productPath, label: product.name })),
  company: [
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Support' },
  ],
  // Parent legal routes. /privacy is the directory of the app-specific
  // notices established in T05; /dpdpa explains how to send a request.
  legal: [
    { to: '/privacy', label: 'Legal' },
    { to: '/dpdpa', label: 'Data Protection' },
  ],
};

// Existing verified studio profiles only; no placeholder or unverified profile.
export const SOCIAL_LINKS = [
  { href: 'https://x.com/tickleapps/', label: 'X', icon: 'fa-x-twitter' },
  { href: 'https://www.instagram.com/tickleapps/', label: 'Instagram', icon: 'fa-instagram' },
  { href: 'https://www.linkedin.com/in/tickle-apps-092789407/', label: 'LinkedIn', icon: 'fa-linkedin-in' },
];

export const HOME = {
  eyebrow: 'Independent app and solutions studio',
  heading: 'Focused software for everyday decisions and disciplined delivery.',
  primaryCta: { label: 'Explore our products', to: '/apps' },
  secondaryCta: { label: 'About TickleApps', to: '/about' },
  productsHeading: 'Products',
};

export const APPS_PAGE = {
  eyebrow: 'Products',
  title: 'Products',
};

export const ABOUT_PAGE = {
  eyebrow: 'About',
  title: 'About TickleApps',
  approachHeading: 'How we work',
};

export const SUPPORT_PAGE = {
  eyebrow: 'Support',
  title: 'Support',
  lead: 'Questions about a TickleApps product? Contact support and tell us which app you use.',
  formHeading: 'Send us a message',
  directHeading: 'Reach us directly',
};
